import { isPlatformBrowser } from '@angular/common';
import { DOCUMENT, Injectable, PLATFORM_ID, Type, inject } from '@angular/core';

import {
    DeviceService,
    DynamicComponentsRegistry,
    DynamicLayoutService,
    HeaderService,
    MenuActionsService,
    OnFeatureInit,
    ProductService,
} from '@frontend/vanilla/core';
import { HeaderSearchService } from '@frontend/vanilla/shared/header';
import { firstValueFrom } from 'rxjs';

import { GlobalSearchClientConfig } from '../../config/client-config';
import { StickyEntryComponent } from '../../entrypoints/sticky-entry.component';
import { __BOOTSTRAP_APP__, __LAUNCH_SEARCH__ } from '../../events';
import { SearchIndexLoaderRegistrationService } from '../indexing/index-loader-registration.service';
import { GlobalSearchWebTrackingService } from '../webanalytics/trackingservice';
import { GlobalSearchBootstrapGuard } from './bootstrap-guard.service';

@Injectable()
export class GlobalSearchBootstrapService implements OnFeatureInit {
    private readonly dynamicComponentsRegistry = inject(DynamicComponentsRegistry);
    private readonly tracking = inject(GlobalSearchWebTrackingService);
    private readonly deviceService = inject(DeviceService);
    private readonly headerService = inject(HeaderService);
    private readonly menuActionsService = inject(MenuActionsService);
    private readonly headerSearchService = inject(HeaderSearchService);
    private readonly searchIndexLoaderRegistrationService = inject(SearchIndexLoaderRegistrationService);
    private readonly globalSearchConfig = inject(GlobalSearchClientConfig);
    private readonly dynamicLayoutService = inject(DynamicLayoutService);
    private readonly bootstrapGuard = inject(GlobalSearchBootstrapGuard);

    bottom: string | undefined;
    isEnabled: false | undefined;
    private readonly documentRef = inject(DOCUMENT);
    private product = inject(ProductService);
    private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

    async onFeatureInit(): Promise<any> {
        if (this.isBrowser) {
            // Use the bootstrap guard to prevent multiple initialization
            await this.bootstrapGuard.waitForBootstrap(() => this.initFeature());
        }
    }

    private async initFeature(): Promise<any> {
        const module = await import('../../entrypoints/header-leftmenu-entry.component').then((x) => x.HeaderLeftMenuEntryComponent);
        const GS_COMPONENTS_MAP: { [key: string]: () => Promise<Type<any>> } = {
            'gs-search': () => Promise.resolve(module),
        };

        this.tracking.registerHandlers();
        if (GS_COMPONENTS_MAP['gs-search'] && !this.deviceService.isMobile) {
            this.dynamicComponentsRegistry.registerLazyComponent('HEADER', 'global-search', GS_COMPONENTS_MAP['gs-search']);
        }
        this.headerService.whenReady.subscribe(() => {
            this.headerSearchService.clickEvent.subscribe(() => {
                if (['casino', 'promo', 'bingo'].includes(this.product.current.name.toLocaleLowerCase())) {
                    dispatchEvent(
                        new CustomEvent(__LAUNCH_SEARCH__, {
                            detail: {
                                launchChannel: 'mobile',
                                launchedFrom: 'epcot',
                            },
                        }),
                    );
                }
            });
        });

        this.menuActionsService.register('openGlobalSearch', () => {
            if (['casino', 'promo', 'bingo'].includes(this.product.current.name.toLocaleLowerCase())) {
                dispatchEvent(
                    new CustomEvent(__LAUNCH_SEARCH__, {
                        detail: {
                            launchChannel: 'mobile',
                            launchedFrom: 'bottom',
                        },
                    }),
                );
            }
        });

        if (this.deviceService.isTouch) {
            this.dynamicLayoutService.addComponent('app', StickyEntryComponent, null);
        }
        await firstValueFrom(this.globalSearchConfig.whenReady);
        addEventListener(__BOOTSTRAP_APP__, () => {
            this.globalSearchConfig.assetPaths?.scripts.forEach((scriptpath) => {
                const trimmedScriptPath = (scriptpath || '')?.trim();
                if (trimmedScriptPath) {
                    // Check if script with the same src is already present in the body
                    const existingScript = this.documentRef.body.querySelector(`script[src="${trimmedScriptPath}"]`);
                    if (!existingScript) {
                        const scriptElement = this.documentRef.createElement('script');
                        scriptElement.src = trimmedScriptPath;
                        this.documentRef.body.append(scriptElement);
                    }
                }
            });
        });
        this.searchIndexLoaderRegistrationService.register();
    }
}
